/**
 * Enregistrement du dossier côté beintravel (back-office hôte).
 *
 * Le widget crée d'abord le dossier chez FerryGate (`POST .../organisation/
 * {org}/ticket`), PUIS le réplique côté beintravel via ce helper. beintravel
 * vit sur un autre host que l'API FerryGate (`store.beintravelUrl`), donc on
 * utilise un `fetch` autonome plutôt que le client FerryGate.
 *
 * Robustesse : timeout dur (AbortController) + petit retry à backoff pour
 * absorber un hoquet réseau / 5xx transitoire, sans jamais re-créer le dossier
 * FerryGate (la création FerryGate est gardée en amont par `if (!store.booking)`).
 * `credentials: 'include'` : le widget tourne dans le `window` de l'hôte, donc
 * la session/cookie beintravel part automatiquement vers son propre backend.
 */

export interface BeintravelSaveOptions {
  /** Timeout par tentative (ms). */
  timeoutMs?: number
  /** Nombre de tentatives supplémentaires après le 1er essai. */
  retries?: number
}

const DEFAULT_TIMEOUT_MS = 12_000
const DEFAULT_RETRIES = 2

export class BeintravelSaveError extends Error {
  constructor(message: string, readonly httpStatus?: number) {
    super(message)
    this.name = 'BeintravelSaveError'
  }
}

/** Réponse de l'endpoint d'enregistrement bein (actionSave, via l'hôte). */
export interface BeinSaveResult {
  success: boolean
  id?: string | null
  reference?: string | null
  payment_url?: string | null
  idempotent?: boolean
  error?: string
}

/**
 * POST le dossier vers beintravel avec timeout + retry, et renvoie le résultat
 * parsé (`id` du dossier bein, `reference`, `payment_url`). Throw
 * `BeintravelSaveError` après épuisement des tentatives. Idempotent côté bein
 * (clé = `ferrygate_booking_id`) — un retry après un 2xx perdu sur le réseau
 * ne crée pas de doublon (actionSave renvoie le dossier existant).
 */
export async function saveBookingToBein(
  url: string,
  payload: unknown,
  opts: BeintravelSaveOptions = {},
): Promise<BeinSaveResult> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS
  const retries = opts.retries ?? DEFAULT_RETRIES

  let lastError: unknown = null
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) {
      // Backoff linéaire borné : 400ms, 800ms, … — évite de marteler un backend
      // momentanément indisponible.
      await delay(Math.min(400 * attempt, 2_000))
    }
    try {
      return await postOnce(url, payload, timeoutMs)
    } catch (err) {
      lastError = err
      // 4xx (hors 408/429) = erreur déterministe : un retry ne changera rien.
      if (err instanceof BeintravelSaveError && isNonRetriable(err.httpStatus)) {
        throw err
      }
    }
  }
  throw lastError instanceof BeintravelSaveError
    ? lastError
    : new BeintravelSaveError(lastError instanceof Error ? lastError.message : 'beintravel save failed')
}

async function postOnce(url: string, payload: unknown, timeoutMs: number): Promise<BeinSaveResult> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
      signal: controller.signal,
    })
    if (!res.ok) {
      throw new BeintravelSaveError(`beintravel save failed (HTTP ${res.status})`, res.status)
    }
    const body = (await res.json().catch(() => null)) as BeinSaveResult | null
    // Succès = `success:true`. On NE retry PAS sur un `id` manquant (déterministe,
    // un retry ne le ferait pas apparaître → évite le triple appel observé).
    if (!body || body.success !== true) {
      throw new BeintravelSaveError(body?.error || 'beintravel save failed')
    }
    return body
  } catch (err) {
    if (err instanceof BeintravelSaveError) throw err
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new BeintravelSaveError(`beintravel save timed out after ${timeoutMs}ms`)
    }
    throw new BeintravelSaveError(err instanceof Error ? err.message : 'beintravel network error')
  } finally {
    clearTimeout(timer)
  }
}

function isNonRetriable(status?: number): boolean {
  if (typeof status !== 'number') return false
  if (status === 408 || status === 429) return false // timeout / rate-limit → retriable
  return status >= 400 && status < 500
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}